import { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  useForm,
  SupportedCurrencies,
  FieldData,
  TextField,
  Select,
  sanitizeToNumeric,
  PortalSubAccount,
  SubAccountsRequest,
  SubAccountsResponse,
  formattedCurrency,
} from 'vopay';
import { AccountTransferRequest } from './types';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useAccountBalance from '@/hooks/useApi/useAccountBalance';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';
import useFetchPortalSubAccounts from '@/hooks/useApi/useFetchPortalSubAccounts';

const SUB_ACCOUNTS_LIMIT = 50;
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

type AccountTransferProps = {
  currency: SupportedCurrencies;
  token: string;
  accountId?: string;
};

export function useAccountTransfer() {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { state: accountTransferState } = useLocation<AccountTransferProps>();
  const accountIdProp = accountTransferState?.accountId;
  const currencyProp = accountTransferState?.currency;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { fetchSubAccounts, transferAccountFrom, transferAccountTo } = useSubAccounts();
  const { balance, fetchBalance } = useAccountBalance();
  const [isUnderReview, setIsUnderReview] = useState(false);

  const currency = currencyProp || (response.Country === 'US' ? 'USD' : 'CAD');
  const selectedAccount = accountIdProp ?? '';

  const setFieldValueRef = useRef<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >(null);

  const fieldDataList = useFormConfig(
    response.AccountID,
    response.AccountName,
    balance?.AvailableFunds?.toString() ?? '0.00',
    fetchSubAccounts,
    selectedAccount,
    currency,
    setFieldValueRef,
  );

  const { renderFormElements, ctx } = useForm<AccountTransferRequest>({
    config: fieldDataList,
    initialValues: {
      Currency: currency,
      From: selectedAccount,
      To: '',
      Amount: 0,
    },
    onSubmit: async (serializedData) => {
      const redirectToTransferDetails = (transactionId: string) => {
        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${transactionId}/${ctx.values.Currency}/`,
          state: {
            transactionId,
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });
      };

      const callTransferTo = async () =>
        transferAccountTo({
          Amount: serializedData.Amount,
          RecipientAccountID: ctx.values.To,
          Currency: ctx.values.Currency,
        });

      const callTransferFrom = async () =>
        transferAccountFrom({
          Amount: serializedData.Amount,
          DebitorAccountID: ctx.values.From,
          Currency: ctx.values.Currency,
        });

      try {
        setDataFetchingInfo(true);

        const fromMain = response.AccountID === ctx.values.From;
        const toMain = response.AccountID === ctx.values.To;

        if (fromMain) {
          const res = await callTransferTo();
          redirectToTransferDetails(res.TransactionID);
        } else if (toMain) {
          const res = await callTransferFrom();
          redirectToTransferDetails(res.TransactionID);
        } else {
          await callTransferFrom();
          const res = await callTransferTo();
          redirectToTransferDetails(res.TransactionID);
        }
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('transferErrorMessage', { defaultValue: 'Failed sub-account transfer' });
        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
    isReviewing: isUnderReview,
  });

  setFieldValueRef.current = ctx.setFieldValue;

  useEffect(() => {
    const getBalance = async () => {
      try {
        await fetchBalance(ctx.values.Currency || 'CAD');
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('balanceErrorMessage', { defaultValue: 'Failed to fetch the account balance' });
        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      }
    };
    getBalance();
  }, [ctx.values.Currency]);

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config: fieldDataList }),
    isUnderReview,
    setIsUnderReview,
  };
}

function useFormConfig(
  mainAccountId: string,
  mainAccountName: string,
  mainAccountBalance: string,
  fetchAccountDetail: (params: SubAccountsRequest) => Promise<SubAccountsResponse>,
  initialFromAccountId: string,
  initialCurrency: string,
  setFieldValueRef: React.RefObject<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >,
): FieldData<AccountTransferRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [selectedDebitorAccountID, setSelectedDebitorAccountID] = useState(initialFromAccountId);
  const [selectedRecipientAccountID, setSelectedRecipientAccountID] = useState('');
  const [fromLabel, setFromLabel] = useState('');
  const [toLabel, setToLabel] = useState('');
  const [fromSearchText, setFromSearchText] = useState('');
  const [toSearchText, setToSearchText] = useState('');

  const fromList = useFetchPortalSubAccounts();
  const toList = useFetchPortalSubAccounts();

  const resolveRequestId = useRef(0);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        await Promise.all([
          fromList.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
          toList.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
        ]);
      } catch (error) {
        console.error(error);
      }
    };
    void loadInitial();
  }, []);

  async function resolveAccount(accountId: string, currencySelected: string) {
    if (accountId === mainAccountId) {
      return {
        label: `${mainAccountName} ($${formattedCurrency(mainAccountBalance || '0.00')})`,
        balance: mainAccountBalance || '0.00',
      };
    }

    const res = await fetchAccountDetail({ ActiveOnly: true, Limit: 1, SubaccountID: accountId });
    const found = res.Subaccounts.find((account) => account.AccountID === accountId);

    if (!found) return { label: accountId, balance: '0.00' };

    const balance =
      Object.values(found.Balances || {}).find((bal) => bal.Currency === currencySelected)?.AvailableBalance || '0.00';

    // Match the dropdown option label, which uses the list endpoint's
    // AccountName (OriginatorName ?? AccountName).
    return { label: `${found.OriginatorName || found.AccountName} ($${formattedCurrency(balance)})`, balance };
  }

  useEffect(() => {
    if (!selectedDebitorAccountID) {
      setFromLabel('');
      setFieldValueRef.current?.('FromBalance', undefined, false);
      return;
    }

    // Guard against out-of-order/stale resolutions: only the newest request may
    // write state (covers rapid re-runs and unmount).
    const requestId = ++resolveRequestId.current;
    resolveAccount(selectedDebitorAccountID, selectedCurrency)
      .then(({ label, balance }) => {
        if (requestId !== resolveRequestId.current) return;
        setFromLabel(label);
        setFieldValueRef.current?.('FromBalance', balance, false);
      })
      .catch((error) => {
        if (requestId !== resolveRequestId.current) return;
        console.error(error);
        setFromLabel('');
        setFieldValueRef.current?.('FromBalance', undefined, false);
      });
  }, [selectedDebitorAccountID, selectedCurrency, mainAccountBalance]);

  const CurrencyOptions = [
    { label: 'CAD', value: 'CAD' },
    { label: 'USD', value: 'USD' },
  ];

  const fromAccountsOptions = buildSubAccountOptions(
    fromList.subAccounts,
    selectedRecipientAccountID,
    selectedRecipientAccountID === mainAccountId,
    fromSearchText,
  );
  const toAccountsOptions = buildSubAccountOptions(
    toList.subAccounts,
    selectedDebitorAccountID,
    selectedDebitorAccountID === mainAccountId,
    toSearchText,
  );

  return [
    {
      label: () => t('currency', { defaultValue: 'Currency' }),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            ctx.setFieldValue('Currency', e.target.value);
            setSelectedCurrency(e.target.value);
          },
        });
        return <Select version="2" options={CurrencyOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('amount', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: AccountTransferRequest) => Number(sanitizeToNumeric(String(values.Amount))),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="currency" {...fieldData} />;
      },
      validate: (ctx) => {
        // Read the balance from the form; the schema rebuilds whenever ctx
        // changes, so this reflects the currently-resolved "From" balance.
        const availableBalance = ctx.values.FromBalance;

        return Yup.string()
          .test(
            'amount-min-validation',
            t('amountValidation', { defaultValue: 'This value should be greater than or equal to $0.01.' }),
            (value) => {
              const amountValidation = Number(value) <= 0.0;
              return !amountValidation;
            },
          )
          .test(
            'amount-max-validation',
            `${t('maxAmountValidation', { defaultValue: 'Insufficient funds. Maximum allowed is:' })} $${String(availableBalance ?? '0.00')}`,
            (value) => {
              if (availableBalance === undefined) return true;
              const max = Number(availableBalance);
              if (Number.isNaN(max)) return true;
              return Number(sanitizeToNumeric(value)) <= max;
            },
          )
          .required();
      },
    },
    {
      label: () => t('from', { defaultValue: 'From' }),
      fieldName: 'From',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            sort={false}
            filterable
            loading={fromList.fetching}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            emptyFilterMsg={t('noSubAccounts', { defaultValue: 'No sub-accounts found' })}
            options={fromAccountsOptions}
            onFilter={(e) => {
              e.preventDefault();
              setFromSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(
                () => fromList.fetchPortalSubAccounts({ SearchText: e.filter, Limit: SUB_ACCOUNTS_LIMIT }),
                SEARCH_DEBOUNCE_MS,
              );
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{fromLabel || value}</>;
            }}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedDebitorAccountID(value);
            }}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('to', { defaultValue: 'To' }),
      fieldName: 'To',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            sort={false}
            filterable
            loading={toList.fetching}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            emptyFilterMsg={t('noSubAccounts', { defaultValue: 'No sub-accounts found' })}
            options={toAccountsOptions}
            onFilter={(e) => {
              e.preventDefault();
              setToSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(
                () => toList.fetchPortalSubAccounts({ SearchText: e.filter, Limit: SUB_ACCOUNTS_LIMIT }),
                SEARCH_DEBOUNCE_MS,
              );
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{toLabel || value}</>;
            }}
            {...fieldData}
            onChange={async (e, { value }) => {
              fieldData.onChange(e);
              setSelectedRecipientAccountID(value);
              if (!value) {
                setToLabel('');
                return;
              }
              try {
                const { label } = await resolveAccount(value, ctx.values.Currency);
                setToLabel(label);
              } catch (error) {
                console.error(error);
                setToLabel('');
              }
            }}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];

  function buildSubAccountOptions(
    lists: PortalSubAccount[],
    excludeAccountId: string,
    removeMain: boolean,
    searchText: string,
  ): { label: string; value: string }[] {
    const isSearching = searchText.length >= SEARCH_MIN_CHARS;
    const mainMatches = mainAccountName.toLowerCase().includes(searchText.toLowerCase());
    const initial: { label: string; value: string }[] =
      removeMain || (isSearching && !mainMatches) ? [] : [{ label: mainAccountName, value: mainAccountId }];

    return [
      ...initial,
      ...lists
        .filter((data) => data.APIAccountID !== excludeAccountId)
        .map((data) => ({ label: data.AccountName, value: data.APIAccountID })),
    ];
  }
}
